/**
 * Live clock — a subscription to ONLY the time fields of the `projects` replicant.
 *
 * While the clock runs the server writes to the replicant every second. `useActiveProject`
 * swallows that tick (clockAgnosticEquals) — i.e. the `state` object passed
 * down does not change and the whole tree does not re-render. Components that
 * DISPLAY the time subscribe through this hook instead: the tick renders only
 * that component.
 *
 * The places that record events (QuickEvents, VAR) cannot rely on the minute
 * in `state` — it is free of ticks, i.e. it may be a few seconds
 * stale. They take the live value with `readLiveClock()` at the moment of the press.
 */
import { getNodeCG } from "@livcg/nodecg-react";
import type { MatchPhase, ProjectsReplicant } from "@livcg/schema";
import { useEffect, useState } from "react";

export type LiveClock = {
  minute: number;
  second: number;
  running: boolean;
  phase: MatchPhase;
  additionalTime: number;
  extraTimeOnAir: boolean;
};

const EMPTY: LiveClock = {
  minute: 0,
  second: 0,
  running: false,
  phase: "pre_match",
  additionalTime: 0,
  extraTimeOnAir: false,
};

function select(record: ProjectsReplicant | undefined, slug: string | null): LiveClock {
  const m = slug && record ? record[slug]?.match : null;
  if (!m) return EMPTY;
  return {
    minute: m.minute,
    second: m.second,
    running: m.running,
    phase: m.phase,
    additionalTime: m.additionalTime,
    extraTimeOnAir: m.extraTimeOnAir,
  };
}

function equal(a: LiveClock, b: LiveClock): boolean {
  return (
    a.minute === b.minute &&
    a.second === b.second &&
    a.running === b.running &&
    a.phase === b.phase &&
    a.additionalTime === b.additionalTime &&
    a.extraTimeOnAir === b.extraTimeOnAir
  );
}

/** The live clock at the moment of the press — reads straight from the replicant. */
export function readLiveClock(slug: string): LiveClock {
  const nodecg = getNodeCG();
  if (!nodecg) return EMPTY;
  return select(nodecg.Replicant<ProjectsReplicant>("projects").value, slug);
}

/** Subscribes to the clock fields; other changes do not render. */
export function useLiveClock(slug: string | null): LiveClock {
  const [clock, setClock] = useState<LiveClock>(() =>
    select(getNodeCG()?.Replicant<ProjectsReplicant>("projects").value, slug),
  );

  useEffect(() => {
    const nodecg = getNodeCG();
    if (!nodecg) return;
    const rep = nodecg.Replicant<ProjectsReplicant>("projects");
    let prev = select(rep.value, slug);
    setClock(prev);
    const handler = (value: ProjectsReplicant | undefined) => {
      const next = select(value, slug);
      if (equal(prev, next)) return;
      prev = next;
      setClock(next);
    };
    rep.on("change", handler);
    return () => {
      rep.removeListener?.("change", handler);
    };
  }, [slug]);

  return clock;
}
